import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Col, DatePicker, Form, Input, InputNumber, List, Modal, Popconfirm,
  Row, Select, Space, Statistic, Switch, Table, Tag, Tooltip, Typography, message,
} from 'antd';
import {
  CheckCircleOutlined, DeleteOutlined, EditOutlined, HistoryOutlined, PlusOutlined,
  ReloadOutlined, SearchOutlined, ThunderboltOutlined, UndoOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { API_BASE_URL } from '../../utils/config';
import { authHeaders } from '../../utils/auth';
import { formatMoney, inputNumberMoneyProps } from '../../utils/currency';
import { useTheme } from '../../context/ThemeContext';

const { Text } = Typography;
const { TextArea } = Input;

const AY_ADLARI = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const AY_KISA = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

const hataMesaji = (e, varsayilan) => e?.response?.data?.detail || varsayilan;
const tarihMetni = (t) => (t ? dayjs(t).format('DD.MM.YYYY') : '');

/**
 * Süzme enerji ödeme takibi (20.08 toplantısı, madde 10).
 *
 * Süzme: kabin kendi aboneliği yerine mülk sahibinin sayacından beslenir,
 * şirket her ay mülk sahibine sabit bir tutar öder. Bu ekran lokasyon başına
 * yılın 12 ayını tek satırda gösterir; her ay hücresi tıklanınca ödeme kaydı
 * açılır/düzenlenir. Aylık tutar + alıcı `enerji_takip` üzerinde tutulur
 * (PUT /tanim), ödemeler `suzme_enerji_odeme` tablosunda (POST /odeme).
 */
export default function SuzmeEnerjiTab({ gomulu }) {
  const { darkMode } = useTheme();
  const buYil = new Date().getFullYear();

  const [yil, setYil] = useState(buYil);
  const [yillar, setYillar] = useState([buYil]);
  const [satirlar, setSatirlar] = useState([]);
  const [ozet, setOzet] = useState(null);
  const [uyari, setUyari] = useState('');
  const [yukleniyor, setYukleniyor] = useState(false);
  const [arama, setArama] = useState('');

  // Tanım modalı (aylık tutar / alıcı)
  const [tanimKayit, setTanimKayit] = useState(null);
  const [tanimForm] = Form.useForm();
  const [tanimKaydediyor, setTanimKaydediyor] = useState(false);

  // Ödeme modalı (tek ay)
  const [odemeHedef, setOdemeHedef] = useState(null); // { kayit, ay, hucre }
  const [odemeForm] = Form.useForm();
  const [odemeKaydediyor, setOdemeKaydediyor] = useState(false);

  // Geçmiş modalı
  const [gecmisKayit, setGecmisKayit] = useState(null);
  const [gecmis, setGecmis] = useState(null);
  const [gecmisYukleniyor, setGecmisYukleniyor] = useState(false);
  const [gecmisIslem, setGecmisIslem] = useState(null);

  const [yilOlusturuyor, setYilOlusturuyor] = useState(null);

  const yukle = useCallback(async (secilenYil = yil) => {
    setYukleniyor(true);
    try {
      const r = await axios.get(`${API_BASE_URL}/suzme-enerji/lokasyonlar`, {
        ...authHeaders(), params: { yil: secilenYil },
      });
      const d = r.data || {};
      setSatirlar(d.satirlar || []);
      setOzet(d.ozet || null);
      setUyari(d.uyari || '');
      if (Array.isArray(d.yillar) && d.yillar.length) setYillar(d.yillar);
    } catch (e) {
      message.error(hataMesaji(e, 'Süzme enerji verileri yüklenemedi'));
    } finally {
      setYukleniyor(false);
    }
  }, [yil]);

  useEffect(() => { yukle(yil); }, [yil]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtreli = useMemo(() => {
    const q = arama.trim().toLocaleLowerCase('tr-TR');
    if (!q) return satirlar;
    return satirlar.filter((s) => [s.lokasyon_adi, s.lokasyon, s.proje, s.il, s.suzme_odeme_alicisi]
      .some((v) => v !== null && v !== undefined && String(v).toLocaleLowerCase('tr-TR').includes(q)));
  }, [satirlar, arama]);

  // --- Tanım ---------------------------------------------------------------
  const tanimAc = (kayit) => {
    setTanimKayit(kayit);
    tanimForm.setFieldsValue({
      suzme_aylik_tutar: kayit.suzme_aylik_tutar ?? null,
      suzme_odeme_alicisi: kayit.suzme_odeme_alicisi || '',
    });
  };

  const tanimKaydet = async () => {
    let v;
    try { v = await tanimForm.validateFields(); } catch (_) { return; }
    setTanimKaydediyor(true);
    try {
      await axios.put(`${API_BASE_URL}/suzme-enerji/${tanimKayit.lokasyon}/tanim`, {
        suzme_aylik_tutar: v.suzme_aylik_tutar ?? null,
        suzme_odeme_alicisi: v.suzme_odeme_alicisi || null,
      }, authHeaders());
      message.success('Tanım güncellendi');
      setTanimKayit(null);
      yukle();
    } catch (e) {
      message.error(hataMesaji(e, 'Tanım kaydedilemedi'));
    } finally {
      setTanimKaydediyor(false);
    }
  };

  // --- Ödeme ---------------------------------------------------------------
  const odemeAc = (kayit, hucre) => {
    setOdemeHedef({ kayit, ay: hucre.ay, hucre });
    odemeForm.setFieldsValue({
      donem_yil: yil,
      donem_ay: hucre.ay,
      tutar: hucre.id ? hucre.tutar : (kayit.suzme_aylik_tutar ?? null),
      odendi: !!hucre.odendi,
      odeme_tarihi: hucre.odeme_tarihi ? dayjs(hucre.odeme_tarihi) : dayjs(),
      fatura_no: hucre.fatura_no || '',
      aciklama: hucre.aciklama || '',
    });
  };

  const odemeKaydet = async () => {
    let v;
    try { v = await odemeForm.validateFields(); } catch (_) { return; }
    setOdemeKaydediyor(true);
    try {
      await axios.post(`${API_BASE_URL}/suzme-enerji/${odemeHedef.kayit.lokasyon}/odeme`, {
        donem_yil: v.donem_yil,
        donem_ay: v.donem_ay,
        tutar: v.tutar ?? 0,
        odendi: !!v.odendi,
        odeme_tarihi: v.odendi && v.odeme_tarihi ? dayjs(v.odeme_tarihi).format('YYYY-MM-DD') : null,
        fatura_no: v.fatura_no || null,
        aciklama: v.aciklama || null,
      }, authHeaders());
      message.success('Ödeme kaydedildi');
      setOdemeHedef(null);
      yukle();
    } catch (e) {
      message.error(hataMesaji(e, 'Ödeme kaydedilemedi'));
    } finally {
      setOdemeKaydediyor(false);
    }
  };

  // --- Yıl oluştur ---------------------------------------------------------
  const yilOlustur = async (kayit) => {
    setYilOlusturuyor(kayit.lokasyon);
    try {
      const r = await axios.post(`${API_BASE_URL}/suzme-enerji/${kayit.lokasyon}/yil-olustur`, {
        yil, tutar: kayit.suzme_aylik_tutar ?? null,
      }, authHeaders());
      const n = r.data?.olusturulan ?? 0;
      message.success(n ? `${yil} için ${n} ay satırı oluşturuldu` : `${yil} için tüm aylar zaten mevcut`);
      yukle();
    } catch (e) {
      message.error(hataMesaji(e, 'Yıl oluşturulamadı'));
    } finally {
      setYilOlusturuyor(null);
    }
  };

  // --- Geçmiş --------------------------------------------------------------
  const gecmisYukle = useCallback(async (kayit) => {
    setGecmisYukleniyor(true);
    try {
      const r = await axios.get(`${API_BASE_URL}/suzme-enerji/${kayit.lokasyon}/odemeler`, authHeaders());
      setGecmis(r.data || null);
    } catch (e) {
      message.error(hataMesaji(e, 'Ödeme geçmişi yüklenemedi'));
    } finally {
      setGecmisYukleniyor(false);
    }
  }, []);

  const gecmisAc = (kayit) => {
    setGecmisKayit(kayit);
    setGecmis(null);
    gecmisYukle(kayit);
  };

  const gecmisKapat = () => {
    setGecmisKayit(null);
    setGecmis(null);
    yukle();
  };

  const odendiDegistir = async (satir, odendi) => {
    setGecmisIslem(satir.id);
    try {
      await axios.post(`${API_BASE_URL}/suzme-enerji/${gecmisKayit.lokasyon}/odeme/${satir.id}/odendi`,
        { odendi }, authHeaders());
      message.success(odendi ? 'Ödendi olarak işaretlendi' : 'Ödenmedi olarak işaretlendi');
      gecmisYukle(gecmisKayit);
    } catch (e) {
      message.error(hataMesaji(e, 'Durum değiştirilemedi'));
    } finally {
      setGecmisIslem(null);
    }
  };

  const odemeSil = async (satir) => {
    setGecmisIslem(satir.id);
    try {
      await axios.delete(`${API_BASE_URL}/suzme-enerji/${gecmisKayit.lokasyon}/odeme/${satir.id}`, authHeaders());
      message.success('Kayıt silindi');
      gecmisYukle(gecmisKayit);
    } catch (e) {
      message.error(hataMesaji(e, 'Kayıt silinemedi'));
    } finally {
      setGecmisIslem(null);
    }
  };

  // --- Tablo ---------------------------------------------------------------
  const ayHucresi = (kayit, hucre) => {
    let etiket;
    if (!hucre || !hucre.id) {
      etiket = <Tag style={{ cursor: 'pointer', margin: 0 }}>—</Tag>;
    } else if (hucre.odendi) {
      etiket = (
        <Tag color="green" style={{ cursor: 'pointer', margin: 0 }}>
          ✓ {formatMoney(hucre.tutar)}
        </Tag>
      );
    } else {
      etiket = <Tag color="gold" style={{ cursor: 'pointer', margin: 0 }}>{formatMoney(hucre.tutar)}</Tag>;
    }
    const ipucu = hucre?.id
      ? `${AY_ADLARI[hucre.ay - 1]} ${yil}: ${hucre.odendi ? `ödendi ${tarihMetni(hucre.odeme_tarihi)}` : 'bekliyor'}`
        + (hucre.fatura_no ? ` · Fatura ${hucre.fatura_no}` : '')
      : `${AY_ADLARI[(hucre?.ay || 1) - 1]} ${yil}: kayıt yok — eklemek için tıklayın`;
    return (
      <Tooltip title={ipucu}>
        <span onClick={() => odemeAc(kayit, hucre)}>{etiket}</span>
      </Tooltip>
    );
  };

  const sutunlar = [
    {
      title: 'Lokasyon', key: 'lokasyon', fixed: 'left', width: 220,
      sorter: (a, b) => String(a.lokasyon_adi || '').localeCompare(String(b.lokasyon_adi || ''), 'tr'),
      render: (_, r) => (
        <div>
          <Text strong>{r.lokasyon_adi || '-'}</Text>
          <div><Text type="secondary" style={{ fontSize: 12 }}>{r.lokasyon}{r.il ? ` · ${r.il}` : ''}</Text></div>
        </div>
      ),
    },
    { title: 'Proje', dataIndex: 'proje', key: 'proje', width: 140, render: (v) => v || '-' },
    {
      title: 'Ödeme alıcısı', dataIndex: 'suzme_odeme_alicisi', key: 'alici', width: 160,
      render: (v) => v || <Text type="secondary">-</Text>,
    },
    {
      title: 'Aylık tutar', dataIndex: 'suzme_aylik_tutar', key: 'aylik', width: 140, align: 'right',
      render: (v, r) => (
        <Space size={4}>
          <span>{formatMoney(v)}</span>
          <Tooltip title={r.enerji_takip_id ? 'Tanımı düzenle' : 'Enerji süreci kaydı yok; tanım girilemez'}>
            <Button size="small" type="text" icon={<EditOutlined />} disabled={!r.enerji_takip_id}
              onClick={() => tanimAc(r)} />
          </Tooltip>
        </Space>
      ),
    },
    ...AY_KISA.map((ad, i) => ({
      title: ad, key: `ay${i + 1}`, width: 112, align: 'center',
      render: (_, r) => ayHucresi(r, (r.aylar || [])[i] || { ay: i + 1 }),
    })),
    {
      title: 'Yıl Toplam', dataIndex: 'yil_toplam_tutar', key: 'toplam', width: 130, align: 'right',
      sorter: (a, b) => (a.yil_toplam_tutar || 0) - (b.yil_toplam_tutar || 0),
      render: (v) => <Text strong>{formatMoney(v)}</Text>,
    },
    {
      title: 'Ödenen', dataIndex: 'yil_odenen', key: 'odenen', width: 130, align: 'right',
      render: (v) => <Text style={{ color: '#389e0d' }}>{formatMoney(v)}</Text>,
    },
    {
      title: 'Bekleyen', dataIndex: 'yil_bekleyen', key: 'bekleyen', width: 130, align: 'right',
      sorter: (a, b) => (a.yil_bekleyen || 0) - (b.yil_bekleyen || 0),
      render: (v, r) => (
        <Space size={4}>
          <Text style={{ color: v > 0 ? '#d48806' : undefined }}>{formatMoney(v)}</Text>
          {r.bekleyen_ay_sayisi > 0 && <Tag color="gold" style={{ margin: 0 }}>{r.bekleyen_ay_sayisi} ay</Tag>}
        </Space>
      ),
    },
    {
      title: 'İşlem', key: 'islem', width: 200, fixed: 'right',
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title={`${yil} için eksik ay satırlarını aylık tutarla oluştur`}>
            <Button size="small" icon={<PlusOutlined />} loading={yilOlusturuyor === r.lokasyon}
              disabled={!!uyari} onClick={() => yilOlustur(r)}>
              Yılı oluştur
            </Button>
          </Tooltip>
          <Button size="small" icon={<HistoryOutlined />} onClick={() => gecmisAc(r)}>Geçmiş</Button>
        </Space>
      ),
    },
  ];

  const odendiSecili = Form.useWatch('odendi', odemeForm);

  return (
    <div style={{ padding: gomulu ? 0 : 16 }}>
      <Card
        className={darkMode ? 'dark-card' : ''}
        bordered={!gomulu}
        title={
          <Space wrap>
            <ThunderboltOutlined />
            <span>Süzme Enerji Ödemeleri</span>
            <Select
              value={yil}
              onChange={setYil}
              style={{ width: 110 }}
              options={yillar.map((y) => ({ value: y, label: String(y) }))}
            />
          </Space>
        }
        extra={
          <Space wrap>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Lokasyon / proje / alıcı ara"
              value={arama}
              onChange={(e) => setArama(e.target.value)}
              style={{ width: 240 }}
            />
            <Button icon={<ReloadOutlined />} onClick={() => yukle()} loading={yukleniyor}>Yenile</Button>
          </Space>
        }
      >
        {uyari && <Alert type="warning" showIcon message={uyari} style={{ marginBottom: 12 }} />}

        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
          <Col xs={12} sm={8} md={4}>
            <Statistic title="Lokasyon" value={ozet?.lokasyon_sayisi ?? satirlar.length} />
          </Col>
          <Col xs={12} sm={8} md={5}>
            <Statistic title={`${yil} toplam`} value={formatMoney(ozet?.toplam_tutar ?? 0)} />
          </Col>
          <Col xs={12} sm={8} md={5}>
            <Statistic title="Ödenen" value={formatMoney(ozet?.odenen_tutar ?? 0)} valueStyle={{ color: '#389e0d' }} />
          </Col>
          <Col xs={12} sm={8} md={5}>
            <Statistic title="Bekleyen" value={formatMoney(ozet?.bekleyen_tutar ?? 0)}
              valueStyle={{ color: (ozet?.bekleyen_tutar ?? 0) > 0 ? '#d48806' : undefined }} />
          </Col>
          <Col xs={12} sm={8} md={5}>
            <Statistic title="Bekleyen ay" value={ozet?.bekleyen_ay_sayisi ?? 0} />
          </Col>
        </Row>

        <Table
          size="small"
          rowKey="lokasyon"
          loading={yukleniyor}
          columns={sutunlar}
          dataSource={filtreli}
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} lokasyon` }}
          locale={{ emptyText: 'Süzme enerji lokasyonu bulunamadı (enerji tipi "süzme" olan süreç yok).' }}
        />
      </Card>

      {/* Tanım modalı */}
      <Modal
        open={!!tanimKayit}
        title={`Tanım — ${tanimKayit?.lokasyon_adi || tanimKayit?.lokasyon || ''}`}
        onCancel={() => setTanimKayit(null)}
        onOk={tanimKaydet}
        okText="Kaydet"
        cancelText="Vazgeç"
        confirmLoading={tanimKaydediyor}
        destroyOnClose
      >
        <Form form={tanimForm} layout="vertical">
          <Form.Item name="suzme_aylik_tutar" label="Aylık tutar (₺)">
            <InputNumber {...inputNumberMoneyProps} min={0} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="suzme_odeme_alicisi" label="Ödeme alıcısı (kişi / kurum)">
            <Input maxLength={150} placeholder="Mülk sahibi, site yönetimi vb." />
          </Form.Item>
          {tanimKayit?.suzme_firma && (
            <Text type="secondary">Eski kayıt (kurulum bilgileri): {tanimKayit.suzme_firma}</Text>
          )}
        </Form>
      </Modal>

      {/* Ödeme modalı */}
      <Modal
        open={!!odemeHedef}
        title={odemeHedef
          ? `Ödeme — ${odemeHedef.kayit.lokasyon_adi || odemeHedef.kayit.lokasyon} · ${AY_ADLARI[odemeHedef.ay - 1]} ${yil}`
          : 'Ödeme'}
        onCancel={() => setOdemeHedef(null)}
        onOk={odemeKaydet}
        okText="Kaydet"
        cancelText="Vazgeç"
        confirmLoading={odemeKaydediyor}
        destroyOnClose
      >
        <Form form={odemeForm} layout="vertical">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="donem_yil" label="Yıl" rules={[{ required: true, message: 'Yıl gerekli' }]}>
                <InputNumber min={2000} max={2100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="donem_ay" label="Ay" rules={[{ required: true, message: 'Ay gerekli' }]}>
                <Select options={AY_ADLARI.map((a, i) => ({ value: i + 1, label: a }))} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="tutar" label="Tutar (₺)" rules={[{ required: true, message: 'Tutar gerekli' }]}>
            <InputNumber {...inputNumberMoneyProps} min={0} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="odendi" label="Ödendi" valuePropName="checked">
                <Switch checkedChildren="Evet" unCheckedChildren="Hayır" />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item name="odeme_tarihi" label="Ödeme tarihi">
                <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} disabled={!odendiSecili} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="fatura_no" label="Fatura / dekont no">
            <Input maxLength={60} />
          </Form.Item>
          <Form.Item name="aciklama" label="Açıklama">
            <TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Geçmiş modalı */}
      <Modal
        open={!!gecmisKayit}
        title={`Ödeme geçmişi — ${gecmisKayit?.lokasyon_adi || gecmisKayit?.lokasyon || ''}`}
        onCancel={gecmisKapat}
        footer={<Button onClick={gecmisKapat}>Kapat</Button>}
        width={760}
        destroyOnClose
      >
        {gecmis?.uyari && <Alert type="warning" showIcon message={gecmis.uyari} style={{ marginBottom: 12 }} />}
        {gecmis?.ozet && (
          <Row gutter={12} style={{ marginBottom: 12 }}>
            <Col span={8}><Statistic title="Toplam" value={formatMoney(gecmis.ozet.toplam_tutar)} /></Col>
            <Col span={8}><Statistic title="Ödenen" value={formatMoney(gecmis.ozet.odenen_tutar)} valueStyle={{ color: '#389e0d' }} /></Col>
            <Col span={8}>
              <Statistic title="Bekleyen" value={formatMoney(gecmis.ozet.bekleyen_tutar)}
                suffix={gecmis.ozet.bekleyen_ay_sayisi ? <Tag color="gold">{gecmis.ozet.bekleyen_ay_sayisi} ay</Tag> : null}
                valueStyle={{ color: gecmis.ozet.bekleyen_tutar > 0 ? '#d48806' : undefined }} />
            </Col>
          </Row>
        )}
        {Array.isArray(gecmis?.yillar) && gecmis.yillar.length > 0 && (
          <Space wrap style={{ marginBottom: 12 }}>
            {gecmis.yillar.map((y) => (
              <Tag key={y.yil}>
                {y.yil}: {formatMoney(y.yil_odenen)} / {formatMoney(y.yil_toplam_tutar)}
                {y.bekleyen_ay_sayisi ? ` · ${y.bekleyen_ay_sayisi} ay bekliyor` : ''}
              </Tag>
            ))}
          </Space>
        )}
        <List
          size="small"
          loading={gecmisYukleniyor}
          dataSource={gecmis?.satirlar || []}
          locale={{ emptyText: 'Bu lokasyon için ödeme kaydı yok.' }}
          style={{ maxHeight: 420, overflowY: 'auto' }}
          renderItem={(s) => (
            <List.Item
              actions={[
                s.odendi ? (
                  <Button key="geri" size="small" icon={<UndoOutlined />} loading={gecmisIslem === s.id}
                    onClick={() => odendiDegistir(s, false)}>Ödenmedi</Button>
                ) : (
                  <Button key="ode" size="small" type="primary" icon={<CheckCircleOutlined />}
                    loading={gecmisIslem === s.id} onClick={() => odendiDegistir(s, true)}>Ödendi</Button>
                ),
                <Popconfirm key="sil" title="Kayıt silinsin mi?" okText="Sil" cancelText="Vazgeç"
                  onConfirm={() => odemeSil(s)}>
                  <Button size="small" danger icon={<DeleteOutlined />} loading={gecmisIslem === s.id} />
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta
                title={
                  <Space>
                    <Text strong>{s.ay_adi || AY_ADLARI[s.donem_ay - 1]} {s.donem_yil}</Text>
                    <Text>{formatMoney(s.tutar)}</Text>
                    {s.odendi
                      ? <Tag color="green">Ödendi{s.odeme_tarihi ? ` · ${tarihMetni(s.odeme_tarihi)}` : ''}</Tag>
                      : <Tag color="gold">Bekliyor</Tag>}
                  </Space>
                }
                description={[
                  s.fatura_no ? `Fatura: ${s.fatura_no}` : null,
                  s.aciklama || null,
                  s.ekleyen_adi ? `Ekleyen: ${s.ekleyen_adi}` : null,
                ].filter(Boolean).join(' · ') || null}
              />
            </List.Item>
          )}
        />
      </Modal>
    </div>
  );
}
